import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import TopNav from "../components/TopNav";
import Footer from "../components/Footer";
import API from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { SubmissionWizard } from "../components/SubmissionWizard";
import { HeritageCareCard } from "../components/HeritageCareCard";
import { Bell, CheckCheck, CheckCircle2 } from "lucide-react";
import logger from "../lib/logger";

export function Notifications() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const nav = useNavigate();
  const load = async () => {
    const { data } = await API.get("/notifications");
    setItems(data.items || []); setUnread(data.unread || 0);
  };
  useEffect(() => { load(); }, []);
  const mark = async (id) => { await API.post(`/notifications/${id}/read`); load(); };
  const markAll = async () => { await API.post("/notifications/read-all"); load(); };
  const openNotif = async (n) => {
    if (!n.read) await API.post(`/notifications/${n.id}/read`);
    if (n.link) nav(n.link);
  };
  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <TopNav />
      <div className="max-w-4xl mx-auto px-6 py-10 flex-1 w-full">
        <div className="flex items-end justify-between mb-6">
          <div>
            <h1 className="font-display text-4xl text-ink flex items-center gap-2"><Bell size={28} /> Notifications</h1>
            <p className="text-ink-soft mt-1">{unread} unread</p>
          </div>
          {unread > 0 && <button onClick={markAll} className="btn-outline text-sm" data-testid="notif-mark-all"><CheckCheck size={14} /> Mark all read</button>}
        </div>
        {items.length === 0 ? (
          <div className="card-heritage text-center py-16">
            <Bell size={40} className="mx-auto text-muted-h mb-3" />
            <div className="font-display text-xl text-ink">You're all caught up</div>
          </div>
        ) : (
          <ul className="space-y-2">
            {items.map((n) => (
              <li key={n.id}>
                <button onClick={() => openNotif(n)} className={`w-full text-left card-heritage flex items-start gap-3 hover:shadow-md ${!n.read ? "!border-l-4 !border-[var(--heritage-terracotta)]" : ""}`} data-testid={`notif-${n.id}`}>
                  {!n.read && <span className="mt-2 w-2 h-2 rounded-full bg-terracotta bg-[var(--heritage-terracotta)]" />}
                  <div className="flex-1">
                    <div className="font-display text-lg text-ink">{n.title}</div>
                    <div className="text-sm text-ink-soft mt-1">{n.body}</div>
                    <div className="text-xs text-muted-h mt-2">{new Date(n.created_at).toLocaleString()}</div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <Footer />
    </div>
  );
}

export function InfoGatherPage() {
  const [enabled, setEnabled] = useState(true);
  useEffect(() => { (async () => {
    try { setEnabled((await API.get("/public/settings")).data.information_gathering_mode); }
    catch (e) { logger.error("Public settings load failed:", e); }
  })(); }, []);
  if (!enabled) return (
    <div className="min-h-screen bg-cream flex flex-col">
      <TopNav />
      <div className="max-w-3xl mx-auto px-6 py-14 flex-1 w-full">
        <h1 className="font-display text-4xl text-ink mb-4">Public Submissions Currently Closed</h1>
        <p className="text-ink-soft mb-6">
          The information-gathering phase is currently disabled. Please create an account to submit a heritage item.
        </p>
        <Link to="/register" className="btn-primary">Create Account</Link>
      </div>
      <Footer />
    </div>
  );
  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <TopNav />
      <div className="max-w-3xl mx-auto px-6 py-10 flex-1 w-full">
        <div className="text-xs uppercase tracking-widest text-muted-h mb-2">Information Gathering — Phase 1</div>
        <h1 className="font-display text-4xl text-ink mb-3">Share What You Know</h1>
        <div className="mb-6 bg-[var(--heritage-parchment)] border-l-4 border-[var(--heritage-gold)] p-4 rounded-r text-sm text-ink-soft leading-relaxed" data-testid="info-notice">
          We are currently gathering information about heritage materials within the community. Submission of information
          does not guarantee review, digitization, preservation, or inclusion in the final Heritage Collection.
          A member of the Heritage team may contact you if additional information is required.
        </div>
        <SubmissionWizard mode="public" />
      </div>
      <Footer />
    </div>
  );
}

export function ThankYou() {
  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <TopNav />
      <div className="flex-1 max-w-3xl mx-auto px-6 py-14 w-full">
        <div className="card-heritage text-center mb-6">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[var(--oih-navy)] text-[var(--oih-gold-soft)] flex items-center justify-center">
            <CheckCircle2 size={32} />
          </div>
          <h1 className="font-display text-4xl md:text-5xl text-navy mb-3">Thank You for Your Submission</h1>
          <p className="text-ink-soft mb-6 max-w-xl mx-auto">
            Your information has been received. A member of the Heritage team may contact you if additional information is required.
          </p>
          <div className="flex gap-2 justify-center flex-wrap">
            <Link to="/" className="btn-outline">Return Home</Link>
            <Link to="/collection" className="btn-primary">Explore the Collection</Link>
          </div>
        </div>
        <HeritageCareCard defaultOpen={false} />
      </div>
      <Footer />
    </div>
  );
}

export function AboutPage() {
  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <TopNav />
      <div className="max-w-3xl mx-auto px-6 py-14 flex-1 w-full">
        <h1 className="font-display text-5xl text-ink mb-6">About the Heritage Project</h1>
        <p className="text-lg text-ink-soft leading-relaxed mb-4">
          The OIH Heritage Project is a community-driven initiative to gather, preserve, and share the
          material and oral heritage of the Ismaili community across generations, continents, and traditions.
        </p>
        <p className="text-lg text-ink-soft leading-relaxed mb-4">
          Every family holds fragments of a shared story — objects, photographs, manuscripts,
          and memories that together form the fabric of our cultural inheritance.
        </p>
        <p className="text-lg text-ink-soft leading-relaxed mb-8">
          Through this platform, community members contribute information about heritage materials.
          Heritage staff and community validators review each submission with care, and approved items
          are shared in a public Heritage Collection for future generations.
        </p>
        <div className="ornament"><span>✦</span></div>
        <div className="text-center mt-8">
          <Link to="/participate" className="btn-primary">How to Participate</Link>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export function ParticipatePage() {
  const [flags, setFlags] = useState({ information_gathering_mode: true });
  useEffect(() => { (async () => {
    try { setFlags((await API.get("/public/settings")).data); }
    catch (e) { logger.error("Public settings load failed:", e); }
  })(); }, []);
  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <TopNav />
      <div className="max-w-3xl mx-auto px-6 py-14 flex-1 w-full">
        <h1 className="font-display text-5xl text-ink mb-6">How to Participate</h1>
        <ol className="space-y-6 text-lg text-ink-soft">
          <li><strong className="text-ink">1. Gather your item.</strong> Photograph or scan the artifact, document, or photograph. Note down what you know about its origin.</li>
          <li><strong className="text-ink">2. Share what you know.</strong> Fill out the submission form — every detail helps, even partial information. You can save drafts and return later.</li>
          <li><strong className="text-ink">3. Wait for review.</strong> Heritage staff and validators review each submission. This may take time.</li>
          <li><strong className="text-ink">4. See it published.</strong> Approved items appear in the public Heritage Collection.</li>
        </ol>
        <div className="mt-10 flex gap-3">
          {flags.information_gathering_mode ? (
            <Link to="/participate/info" className="btn-primary" data-testid="participate-info-cta">Share Information</Link>
          ) : (
            <Link to="/register" className="btn-primary">Create Account</Link>
          )}
          <Link to="/collection" className="btn-outline">Explore Collection</Link>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export function ContactPage() {
  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <TopNav />
      <div className="max-w-2xl mx-auto px-6 py-14 flex-1 w-full text-center">
        <h1 className="font-display text-5xl text-ink mb-6">Get in Touch</h1>
        <p className="text-lg text-ink-soft mb-6">
          The OIH Heritage team is here to help you preserve and share your family's heritage.
        </p>
        <div className="card-heritage text-left">
          <div className="mb-3"><strong className="text-ink">Email:</strong> <a href="mailto:heritage@oih.org" className="text-terracotta hover:underline">heritage@oih.org</a></div>
          <div>If you have an item you'd like help submitting, you can also request assistance directly inside your submission form.</div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
